import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { HeartPulse, Users, ArrowLeft, Search, Loader2, User, Phone, AlertTriangle, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilesPage() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteProfile, setDeleteProfile] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchProfiles();
  }, [navigate]);

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/profiles?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error("Failed to load profiles");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProfile) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/profiles/${deleteProfile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile deleted");
      setProfiles(profiles.filter(p => p.id !== deleteProfile.id));
    } catch (error) {
      toast.error("Failed to delete profile");
    } finally {
      setDeleting(false);
      setDeleteProfile(null);
    }
  };

  const filteredProfiles = profiles.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.bracelet_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Profiles
              </span>
            </div>
          </div>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      <main className="container-app py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or bracelet code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11"
            data-testid="search-input"
          />
        </div>

        {/* Results count */}
        <p className="text-sm text-slate-500 mb-4">
          Showing {filteredProfiles.length} profile{filteredProfiles.length !== 1 ? 's' : ''}
        </p>

        {/* Profiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProfiles.map((profile) => (
            <Card key={profile.id} className="border-slate-200 card-hover" data-testid={`profile-card-${profile.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start gap-4">
                  {profile.photo_base64 ? (
                    <img
                      src={`data:image/jpeg;base64,${profile.photo_base64}`}
                      alt={profile.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-teal-500"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center border-2 border-teal-500">
                      <User className="h-8 w-8 text-teal-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {profile.name}
                    </CardTitle>
                    <p className="text-sm text-slate-500 font-mono">{profile.bracelet_code}</p>
                    {profile.has_dementia && (
                      <Badge className="mt-1 bg-pink-100 text-pink-700 hover:bg-pink-100">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Memory Care
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="h-4 w-4 text-teal-600" />
                    <span>{profile.emergency_contact_name}</span>
                  </div>
                  <p className="text-sm text-slate-500 pl-6">{profile.emergency_contact_phone}</p>
                  {profile.allergies && (
                    <p className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      Allergies: {profile.allergies}
                    </p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Link to={`/emergency/${profile.bracelet_code}`} className="flex-1">
                    <Button size="sm" className="w-full bg-teal-600 hover:bg-teal-700">
                      View
                    </Button>
                  </Link>
                  <Link to={`/dashboard/profiles/${profile.id}/edit`}>
                    <Button size="sm" variant="outline">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => setDeleteProfile(profile)}
                    data-testid={`delete-profile-${profile.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProfiles.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No profiles found</p>
            <Link to="/dashboard/bracelets" className="mt-4 inline-block">
              <Button variant="outline">
                View Bracelets to Register
              </Button>
            </Link>
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProfile} onOpenChange={() => setDeleteProfile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteProfile?.name}'s profile and unlink their bracelet ({deleteProfile?.bracelet_code}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
